import React, { useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import * as authService from '../services/auth';

/**
 * AuthProvider Component
 * 
 * Wraps the application and manages authentication state using the auth service.
 * Integrates with AppContext to provide authentication functionality to all child components.
 * 
 * Responsibilities:
 * - Manage authentication state (isAuthenticated, user, tokens)
 * - Handle Cognito OAuth callback
 * - Implement token refresh before expiration
 * - Provide authentication context to child components
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4
 */

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setAuthState, isAuthenticated, isInitializing, setIsInitializing } = useAppContext();
  const location = useLocation();

  // Initialize authentication state on mount
  useEffect(() => {
    // Check if user is already authenticated (has valid token)
    const token = authService.getToken();
    const user = authService.getCurrentUser();

    if (token && user) {
      // User is authenticated, update context
      setAuthState(user, {
        accessToken: token,
        idToken: '', // ID token not needed after initial auth
        expiresAt: Date.now() + (8 * 60 * 60 * 1000), // 8 hours
      });
    }
    
    // Mark as initialized after checking auth state
    setIsInitializing(false);
  }, [setAuthState, setIsInitializing]);

  // Redirect unauthenticated users to login (except for callback and logout pages)
  useEffect(() => {
    // Don't redirect until we've initialized
    if (isInitializing) {
      return;
    }
    
    const isCallbackPage = location.pathname === '/callback';
    const isLogoutPage = location.pathname === '/logout';
    
    // Don't redirect if on callback or logout page
    if (isCallbackPage || isLogoutPage) {
      // Remove overlay for callback/logout pages
      const overlay = document.getElementById('loading-overlay');
      const root = document.getElementById('root');
      if (overlay) overlay.style.display = 'none';
      if (root) root.classList.add('ready');
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated && !authService.isAuthenticated()) {
      // User is not authenticated, redirect to Cognito login
      // Keep overlay visible during redirect
      authService.login();
    } else {
      // User is authenticated, remove overlay and show dashboard
      const overlay = document.getElementById('loading-overlay');
      const root = document.getElementById('root');
      if (overlay) overlay.style.display = 'none';
      if (root) root.classList.add('ready');
    }
  }, [isAuthenticated, location.pathname, isInitializing]);

  return <>{children}</>;
};

export default AuthProvider;
